import { useSelector } from "react-redux";
import { useCheckoutStateSelector } from "@checkout/context";
import {
  getAccountRewardsPoints,
  getIsRewardsSelected,
  getRewardsAmountToApply,
} from "@hsbc/rewards";
import { getProductTotalPriceInRewards } from "@hsbc/shared";

// The values for these map to i18n keys
export enum PointsErrors {
  // eslint-disable-next-line no-unused-vars
  Valid = "",
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  NotSelected = "",
  InvalidNumber = "rewards.errors.invalidNumber",
  GreaterThanTotalBalance = "rewards.errors.numberGreaterThanTotalBalance",
  GreaterThanTotalCost = "rewards.errors.numberGreaterThanTotalCost",
}

type PointsErrorValidationResult = { isValid: boolean; reason: PointsErrors };

type PointsErrorValidator = (input: {
  totalAccountPoints: number;
  totalCostInPoints?: number;
  rewardsAmountToPay: number;
}) => PointsErrorValidationResult;

export const isValidNumberOfPoints: PointsErrorValidator = ({
  totalAccountPoints,
  totalCostInPoints,
  rewardsAmountToPay,
}) => {
  if (
    rewardsAmountToPay < 0 ||
    isNaN(rewardsAmountToPay) ||
    totalCostInPoints == null
  ) {
    return { isValid: false, reason: PointsErrors.InvalidNumber };
  } else if (rewardsAmountToPay > totalAccountPoints) {
    return { isValid: false, reason: PointsErrors.GreaterThanTotalBalance };
  } else if (rewardsAmountToPay > totalCostInPoints) {
    return { isValid: false, reason: PointsErrors.GreaterThanTotalCost };
  }

  return { isValid: true, reason: PointsErrors.Valid };
};

export const usePointsInputValidator = (): PointsErrorValidationResult => {
  const { value: totalAccountPoints } =
    useSelector(getAccountRewardsPoints) || {};

  const isRewardsSelected = useCheckoutStateSelector(getIsRewardsSelected);
  const rewardsAmountToPay = useCheckoutStateSelector(getRewardsAmountToApply);
  const totalCostInPoints = useCheckoutStateSelector(
    getProductTotalPriceInRewards
  )?.value;

  if (!isRewardsSelected) {
    return { isValid: true, reason: PointsErrors.NotSelected };
  }

  const validationResult = isValidNumberOfPoints({
    totalAccountPoints,
    totalCostInPoints,
    rewardsAmountToPay,
  });

  return validationResult;
};
